import { TreeNodeType, TreeRow } from "@/types/LCAoM/TreeBuilder";
import { AutoAssignCateogryMaterial, AutoAssignMaterialsRequest, AutoAssignNodesMaterial, NodeTypes } from "@api/apiClient";
import { useAutoAssignMaterials as useAutoAssignMaterialsMutation } from "@api/models";
import useSelectionManager from "@hooks/useSelectionManager";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, useCallback } from "react";

/**
 * Maps TreeRow type to NodeTypes enum.
 */
function getNodeTypeFromTreeRowType(rowType: TreeRow["type"]): NodeTypes | undefined {
  switch (rowType) {
    case TreeNodeType.Element:
      return NodeTypes.Element;
    case TreeNodeType.Component:
      return NodeTypes.Component;
    case TreeNodeType.Supplemental:
      return NodeTypes.Supplemental;
    default:
      return undefined;
  }
}

interface UseAutoAssignMaterialsParams {
  clearSelectedNodes: () => Promise<void>;
  assignGridApiRef: MutableRefObject<GridApiPremium>;
  /** Callback invoked after successful auto-assign (e.g., to refetch data) */
  onSuccess?: () => void | Promise<void>;
}

interface UseAutoAssignMaterialsResult {
  handleAutoAssignMaterials: () => Promise<void>;
  isAutoAssignMaterialsPending: boolean;
}

export const useAutoAssignMaterials = ({
  clearSelectedNodes,
  assignGridApiRef,
  onSuccess,
}: UseAutoAssignMaterialsParams): UseAutoAssignMaterialsResult => {
  const selectionManager = useSelectionManager();
  const mutateAutoAssignMaterials = useAutoAssignMaterialsMutation();

  const handleAutoAssignMaterials = useCallback(async () => {
    const scenarioId = selectionManager.selectedScenarioId;

    if (!scenarioId) {
      console.error("ScenarioId is required for auto-assigning materials");
      return;
    }

    // Get selected rows from the grid
    const selectedRowsMap = assignGridApiRef.current?.getSelectedRows();
    if (!selectedRowsMap || selectedRowsMap.size === 0) {
      console.warn("No rows selected for auto-assignment");
      return;
    }

    // Convert to TreeRow array
    const selectedRows: TreeRow[] = [];
    selectedRowsMap.forEach((row) => {
      selectedRows.push(row as TreeRow);
    });

    // Build categories and nodes arrays
    const categories: AutoAssignCateogryMaterial[] = [];
    const nodes: AutoAssignNodesMaterial[] = [];

    // Track selected category IDs to exclude their children from the nodes array
    const selectedCategoryIds = new Set<string>();

    // First pass: identify selected category nodes
    selectedRows.forEach((row) => {
      if (row.type === TreeNodeType.Group && row.field === "cadModelCategoryId" && row.rawValue) {
        // This is a category node - add to categories array
        categories.push(
          new AutoAssignCateogryMaterial({
            scenarioId: scenarioId,
            cadModelCategoryId: row.rawValue,
          }),
        );
        selectedCategoryIds.add(row.rawValue);
      }
    });

    // Second pass: add leaf nodes that are not children of selected categories
    selectedRows.forEach((row) => {
      // Skip group nodes - we only want leaf nodes (elements, components, supplementals)
      if (row.type === TreeNodeType.Group) {
        return;
      }

      // Get the nodeId from row data and derive nodeType from TreeRow.type
      const nodeId = row.data?.nodeId;
      const nodeType = getNodeTypeFromTreeRowType(row.type);
      const cadModelCategoryId = row.data?.cadModelCategoryId;

      // Skip if this node belongs to a selected category (category will handle it)
      if (cadModelCategoryId && selectedCategoryIds.has(cadModelCategoryId)) {
        return;
      }

      if (nodeId != null && nodeType != null) {
        nodes.push(
          new AutoAssignNodesMaterial({
            scenarioId: scenarioId,
            nodeId: Number(nodeId),
            nodeType: nodeType,
          }),
        );
      } else {
        console.warn("Node data missing nodeId or unable to determine nodeType. Skipping node:", row);
      }
    });

    if (categories.length === 0 && nodes.length === 0) {
      console.warn("No valid categories or nodes found for auto-assignment");
      return;
    }

    const autoAssignMaterialsRequest = new AutoAssignMaterialsRequest({
      modelIds: selectionManager.getSelectedModelIds(),
      scenarioId: scenarioId,
      categories: categories.length > 0 ? categories : undefined,
      nodes: nodes.length > 0 ? nodes : undefined,
    });

    await mutateAutoAssignMaterials.mutateAsync(autoAssignMaterialsRequest as any);
    await clearSelectedNodes();
    await onSuccess?.();
  }, [assignGridApiRef, clearSelectedNodes, mutateAutoAssignMaterials, onSuccess, selectionManager]);

  return {
    handleAutoAssignMaterials,
    isAutoAssignMaterialsPending: mutateAutoAssignMaterials.isPending,
  };
};
